import { Container, IInit } from '../../../common/container/Container'
import { Wearable } from '../models/Wearable'
import { ContentContainerConfig } from '../container'
import { Observable, of } from 'rxjs'
import { Query } from '../../../common/api/Query'
import { catchError, map } from 'rxjs/operators'
import { prepareURL } from '../../../common/api/http-helpers'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { WearableDTO, toModel } from '../models/WearableDTO'
import { STATUS_SERVICE_KEY } from '../../../container/app'

export interface IWearableApi extends IInit {
  getByID(id: string): Observable<Wearable | undefined>

  getFilteredList(q: Query<Wearable>): Observable<ItemList<Wearable>>

  add(e: WearableDTO): Observable<Wearable | undefined>

  update(e: WearableDTO): Observable<Wearable | undefined>

  delete(id: string): Observable<boolean>
}

export class WearableApi implements IWearableApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as ContentContainerConfig).moduleFullUrl + '/userWearable'
  }

  add(e: WearableDTO): Observable<Wearable | undefined> {
    return this._httpClient
      .post<Wearable>({ url: this._url, body: e })
      .pipe(
        map<WearableDTO, Wearable>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<Wearable | undefined> {
    return this._httpClient
      .get<Wearable>({ url: `${this._url}/${id}` })
      .pipe(
        map<WearableDTO, Wearable>((d) => toModel(d)),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  getFilteredList(q: Query<Wearable>): Observable<ItemList<Wearable>> {
    let url = this._url
    return this._httpClient
      .get<ItemList<Wearable>>({ url: prepareURL(url, q) })
      .pipe(
        map<ItemList<WearableDTO>, ItemList<Wearable>>((dto) => {
          const itemList = emptyList<Wearable>()
          itemList.count = dto.count
          itemList.items = dto.items.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<Wearable>())
        })
      )
  }

  update(e: WearableDTO): Observable<Wearable | undefined> {
    return this._httpClient
      .put<Wearable>({ url: this._url, body: e })
      .pipe(
        map<WearableDTO, Wearable>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }
}
